import { Inject, Injectable } from '@angular/core';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import {
  AuthnTransaction,
  OktaAuth,
  SigninWithCredentialsOptions,
  TokenResponse,
  UserClaims,
} from '@okta/okta-auth-js';
import {
  Observable,
  ReplaySubject,
  catchError,
  debounceTime,
  filter,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';

/**
 * Service responsible for authenticating a user with Okta.
 * This service should only be injected into AuthService. The rest of the application
 * should depend on AuthService or UserService for authentication state.
 *
 * Supports silent login: if the user has an active Okta session
 * (e.g., logged in via another Genesys app), this service will detect it
 * and authenticate without user interaction.
 */
@Injectable({
  providedIn: 'root',
})
export class OktaService {
  private _isAuthenticated$: ReplaySubject<boolean> = new ReplaySubject(1);
  private _claims$: ReplaySubject<UserClaims> = new ReplaySubject(1);

  constructor(
    private authStateService: OktaAuthStateService,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth
  ) {
    // OktaAuthStateService detects auth state immediately upon construction.
    // This subscription handles auto-login from localStorage tokens.
    // This OktaService is a global service, so auto-login will happen when the app starts.
    this.authStateService.authState$
      .pipe(
        // Debounce the observable by 250 milliseconds to guard against repetitive and fast updates between true/false.
        // Rapid toggling can occur while a sign-in/out process is in an intermediate state during token caching or cleanup.
        debounceTime(250),
        tap((state) => {
          console.debug(`[Services][OktaService] Updated auth state to ${state.isAuthenticated}`);
          this._isAuthenticated$.next(state.isAuthenticated);
        }),
        map((state) => state.isAuthenticated),
        filter((isAuthenticated) => isAuthenticated === true),
        switchMap(() => this.oktaAuth.getUser()),
        catchError((error) => {
          console.error('[Services][OktaService] Error getting user claims:', error);
          return of(null);
        }),
        filter((claims) => claims !== null),
        tap((claims) => this._claims$.next(claims))
      )
      .subscribe({
        error: (error) => {
          console.error('[Services][OktaService] Error in auth state subscription:', error);
          this._isAuthenticated$.next(false);
        },
      });

    // Silent login: if no tokens exist locally, check if the user has an
    // active Okta session (e.g., logged in from another Genesys app). Uses a hidden
    // iframe with prompt=none per the OIDC spec. If a session exists, tokens
    // are obtained silently and stored — authState$ will emit the update.
    // If no session exists, the user simply sees the public/unauthed page.
    this.attemptSilentLogin();
  }

  /**
   * Attempts to silently obtain tokens using an existing Okta session.
   * This enables cross-app SSO: if the user is logged into Okta via any Genesys app,
   * they'll be automatically authenticated here without clicking "login."
   *
   * Uses `token.getWithoutPrompt()` which opens a hidden iframe to Okta's
   * /authorize endpoint with `prompt=none`. This is a standard OIDC mechanism.
   */
  private async attemptSilentLogin(): Promise<void> {
    try {
      // Skip if the app is currently handling an OAuth callback.
      // The OktaCallbackComponent will handle the token exchange on this route.
      // Running getWithoutPrompt() concurrently would cause a PKCE codeVerifier collision.
      if (window.location.pathname.includes('/login/callback')) {
        console.debug('[Services][OktaService] On callback route, skipping silent login.');
        return;
      }

      // Skip if tokens already exist in storage (user is already authenticated)
      const existingTokens = await this.oktaAuth.tokenManager.getTokens();
      if (existingTokens.accessToken || existingTokens.idToken) {
        console.debug('[Services][OktaService] Tokens exist in storage, skipping silent login.');
        return;
      }

      console.debug('[Services][OktaService] No local tokens. Attempting silent login...');
      const response = await this.oktaAuth.token.getWithoutPrompt();
      this.oktaAuth.tokenManager.setTokens(response.tokens);
      console.debug('[Services][OktaService] Silent login successful.');
    } catch (error: any) {
      // Expected when user has no active Okta session — not an error condition.
      console.debug(
        '[Services][OktaService] Silent login not available:',
        error?.errorCode || error?.message || 'no session'
      );
    }
  }

  /**
   * Indicates whether the current user is authenticated. However, it is recommended
   * to use the UserService for this operation to reduce injected dependencies.
   *
   * @returns observable indicating true/false
   */
  get isAuthenticated$(): Observable<boolean> {
    return this._isAuthenticated$.asObservable();
  }

  /**
   * Return Okta User Claim Observable
   * @returns User claims within the Okta JWT.
   */
  get claims$(): Observable<UserClaims> {
    return this._claims$;
  }

  /**
   * Authenticate the user with custom Okta login page
   * @param options has username and password
   * @param authClient Okta Auth library
   * @returns Okta AuthTransaction promise
   */
  async signInWithCredentials(options: SigninWithCredentialsOptions): Promise<void> {
    return this.oktaAuth
      .signInWithCredentials(options)
      .then(async (transaction: AuthnTransaction) => {
        if (transaction.status === 'SUCCESS') {
          return transaction.sessionToken;
        } else if (transaction.status === 'MFA_ENROLL') {
          return Promise.reject(new Error('Cannot login without MFA_ENROLL enabled'));
        } else if (transaction.status === 'MFA_REQUIRED') {
          let isOktaAuthenticator = false;
          let isGoogleAuthenticator = false;
          let isYubicoAuthenticator = false;
          const oktaFactor = transaction.factors.find((factorItem: Record<string, any>) => {
            isOktaAuthenticator = true;
            return factorItem.provider === 'OKTA' && factorItem.factorType === 'push';
          });
          const googleFactor = transaction.factors.find((factorItem: Record<string, any>) => {
            isGoogleAuthenticator = true;
            return (
              factorItem.provider === 'GOOGLE' && factorItem.factorType === 'token:software:totp'
            );
          });
          const yubicoFactor = transaction.factors.find((factorItem: Record<string, any>) => {
            isYubicoAuthenticator = true;
            return factorItem.provider === 'YUBICO' && factorItem.factorType === 'token:hardware';
          });

          if (oktaFactor) {
            const verifiedAuthTransaction = await oktaFactor.verify({ autoPush: true });
            const verifiedPollTransaction = await verifiedAuthTransaction.poll();
            if (verifiedPollTransaction && verifiedPollTransaction.status === 'SUCCESS') {
              return verifiedPollTransaction.sessionToken;
            } else {
              return Promise.reject(
                new Error('Error: cannot handle the ' + verifiedPollTransaction.status + ' status')
              );
            }
          }
          if (googleFactor || yubicoFactor) {
            // NOTE: We currently do not handle customer MFA Challenge here.
            // Prompt user to use Sign In with Okta
            let challengeAuthenticatorError;
            if (googleFactor) {
              challengeAuthenticatorError =
                'Cannot process MFA_CHALLENGE for Google Authenticator.';
            } else {
              challengeAuthenticatorError =
                'Cannot process MFA_CHALLENGE for Yubico Authenticator.';
            }

            return Promise.reject(new Error(challengeAuthenticatorError));
          }
        }
      })
      .then((sessionToken: string) => {
        return this.oktaAuth.token.getWithoutPrompt({
          sessionToken: sessionToken,
        });
      })
      .then((res: TokenResponse) => {
        this.oktaAuth.tokenManager.setTokens(res.tokens);
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  }

  /**
   * Authenticate the user with the Okta default login page
   * @param originalUri string uri path
   */
  async signInWithRedirect(originalUri: string): Promise<void> {
    return this.oktaAuth.signInWithRedirect({
      originalUri,
    });
  }

  /**
   * Clear Tokens
   */
  clearTokens(): void {
    this.oktaAuth.tokenManager.clear();
  }

  /**
   * Return Okta Auth Client Access token
   * @returns Okta Access token - string
   */
  getAccessToken(): string {
    return this.oktaAuth.getAccessToken();
  }

  /**
   * Return Okta Auth Client Id token
   * @returns Okta Access token - string
   */
  getIdToken(): string {
    return this.oktaAuth.getIdToken();
  }

  /**
   *
   * Logout User from Okta and redirect while maintaining the current page the user was on.
   * This is helpful when the Okta cookie expires, the user won't suddenly be redirected back to
   * /home if they are on another page, such as a Course or Subscription page.
   * @param redirectUrl - string
   */
  signOutWithRedirect(redirectUrl: string): void {
    this.oktaAuth.signOut({
      postLogoutRedirectUri: redirectUrl,
    });
  }

  /**
   * Sign out of Okta
   * @returns boolean true on sign out
   */
  signOut(): void {
    this.oktaAuth.signOut();
  }
}
